/**
 * The scroll speed the editor SHOWS (W3-05; UX-035): the effective speed that moves the notes
 * (`Engine.sampleEffectiveSpeed`, dH/dt of Phira's floor curve) up to Phira's `max_time`, and the speed keys' own
 * value after it.
 *
 * Phira freezes the floor position at max_time (the last note or motion event plus 1 s, maxTime.ts), so the
 * effective speed reads 0 there. Nothing moves after max_time only because nothing is there yet: placing a note
 * later extends max_time and the notes then scroll at the keyed speed. Drawing that 0 made a fresh project's Scroll
 * speed column step from 10 to 0 about a second after the last item ("now 0" while the preview said "Speed 10").
 * The motion column, its header readout and the curve editor share this sampler, so they agree with the preview's
 * line readout everywhere a note could be placed.
 */

import type { Engine } from '../engine/types';
import type { LineId } from '../model/types';

let scratch = new Float32Array(0);

/** Time of sample i of n evenly spaced samples over [t0, t1] (t0 alone for one sample), as the engine samplers. */
function timeAt(t0: number, t1: number, i: number, n: number): number {
  return n <= 1 ? t0 : t0 + ((t1 - t0) * i) / (n - 1);
}

/** `out.length` shown scroll speeds (RPE units) over [t0, t1] seconds; see the file header. */
export function sampleShownSpeed(
  engine: Pick<Engine, 'sampleEffectiveSpeed' | 'sampleChannel' | 'maxTime'>,
  line: LineId,
  t0: number,
  t1: number,
  out: Float32Array,
): void {
  engine.sampleEffectiveSpeed(line, t0, t1, out);
  const n = out.length;
  const max = engine.maxTime;
  let needs = false;
  for (let i = 0; i < n && !needs; i++) needs = Number.isNaN(out[i]) || timeAt(t0, t1, i, n) >= max;
  if (!needs) return;
  if (scratch.length < n) scratch = new Float32Array(n);
  const raw = n === scratch.length ? scratch : scratch.subarray(0, n);
  engine.sampleChannel(line, 'speed', 'sum', t0, t1, raw);
  for (let i = 0; i < n; i++) {
    const r = raw[i] ?? Number.NaN;
    if (!Number.isFinite(r)) continue;
    if (Number.isNaN(out[i]) || timeAt(t0, t1, i, n) >= max) out[i] = r;
  }
}

/** The shown scroll speed at one second (header readouts). */
export function shownSpeedAt(
  engine: Pick<Engine, 'sampleEffectiveSpeed' | 'sampleChannel' | 'maxTime'>,
  line: LineId,
  sec: number,
): number {
  const one = new Float32Array(1);
  sampleShownSpeed(engine, line, sec, sec, one);
  return one[0] ?? Number.NaN;
}
